import React, { useState } from 'react';
import {
    TextField,
    Button,
    Box,
    CircularProgress,
    Alert,
    Grid,
    Typography,
    Paper,
} from '@mui/material';
import { businessAPI } from '../api';
import { normalizeMobile } from '../utils/mobile';

// Business phone must be a proper 10-digit Indian mobile (6-9 start).
// A +91 / 91 / 0 prefix is accepted and stripped by normalizeMobile.
// Empty is allowed — the field is optional.
const getPhoneError = (phone) => {
    if (!phone || !phone.trim()) return '';
    const digits = normalizeMobile(phone).replace(/\D/g, '');
    if (digits.length !== 10) return 'Enter a 10-digit mobile number';
    if (!/^[6-9]/.test(digits)) return 'Enter a valid Indian mobile number';
    return '';
};

const BusinessForm = ({ onBusinessCreated, defaultPhone = '' }) => {
    const [formData, setFormData] = useState({
        name: '',
        city: '',
        phone: defaultPhone || '',
    });
    const [businessMode, setBusinessMode] = useState('subscription');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            setError('Business name is required');
            return;
        }
        const phoneErr = getPhoneError(formData.phone);
        if (phoneErr) {
            setError(phoneErr);
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const businessData = {
                f_name: formData.name.trim(),
                f_city: formData.city.trim() || null,
                f_phone: formData.phone.trim() ? normalizeMobile(formData.phone) : null,
                f_business_mode: businessMode,
            };
            const response = await businessAPI.createBusiness(businessData);
            setSuccess('Business created successfully!');
            setTimeout(() => {
                onBusinessCreated(response.data);
            }, 1000);
        } catch (err) {
            let errorMessage = 'Failed to create business';
            if (err.response?.data?.detail) {
                const detail = err.response.data.detail;
                if (Array.isArray(detail)) errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
                else if (typeof detail === 'string') errorMessage = detail;
                else errorMessage = JSON.stringify(detail);
            }
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            required
                            size="small"
                            label="Business Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="e.g. Raj Milk Dairy"
                            autoFocus
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="City (optional)"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            placeholder="e.g. Hyderabad"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            type="tel"
                            label="Phone Number"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="e.g. 9876543210"
                            error={!!getPhoneError(formData.phone)}
                            helperText={getPhoneError(formData.phone) || 'Shown to your customers. Change it if this shop uses a different number.'}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                            What kind of business do you run?
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {[
                                {
                                    value: 'subscription',
                                    emoji: '📦',
                                    title: 'Regular Delivery',
                                    subtitle: 'రోజూ డెలివరీ, నెల తర్వాత బిల్లు',
                                    desc: 'Milk, newspaper, water — delivered daily. Bill is generated only after the month is complete.',
                                },
                                {
                                    value: 'orders',
                                    emoji: '🛍️',
                                    title: 'Order by Order',
                                    subtitle: 'ఆర్డర్ వస్తే, డెలివరీ చేయండి',
                                    desc: 'Customer calls, you deliver and collect payment each time',
                                },
                                {
                                    value: 'both',
                                    emoji: '📦🛍️',
                                    title: 'Both',
                                    subtitle: 'రెగ్యులర్ డెలివరీ + ఒకసారి ఆర్డర్లు',
                                    desc: 'Run subscriptions and take individual orders together',
                                },
                            ].map(opt => (
                                <Paper
                                    key={opt.value}
                                    variant="outlined"
                                    onClick={() => setBusinessMode(opt.value)}
                                    sx={{
                                        p: 1.5,
                                        cursor: 'pointer',
                                        borderWidth: 2,
                                        borderColor: businessMode === opt.value ? 'primary.main' : 'divider',
                                        bgcolor: businessMode === opt.value ? 'primary.50' : 'background.paper',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                    }}
                                >
                                    <Typography sx={{ fontSize: '1.8rem', lineHeight: 1 }}>{opt.emoji}</Typography>
                                    <Box>
                                        <Typography variant="body1" fontWeight="bold" color={businessMode === opt.value ? 'primary.main' : 'text.primary'}>
                                            {opt.title}
                                        </Typography>
                                        <Typography variant="body2" color={businessMode === opt.value ? 'primary.dark' : 'text.secondary'} fontStyle="italic">
                                            {opt.subtitle}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {opt.desc}
                                        </Typography>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={loading || !!getPhoneError(formData.phone)}
                            sx={{ mt: 1 }}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Create Business'}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};

export default BusinessForm;
